import { hasStoredZipCode, shouldShowZipCodeFieldForTaxCalculation } from '@/utils/tax';
import { ZipCode } from '../ZipCode/ZipCode';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useContext, useState, useEffect, useCallback } from 'react';

interface InputTaxComponentProps {
  onValidate?: (isValid: boolean) => void;
  onZipCodeValidate?: (isValid: boolean) => void;
  isBlockedForZipCode?: boolean;
}

export const InputTaxComponent = ({
  onValidate,
  onZipCodeValidate,
  isBlockedForZipCode = false,
}: InputTaxComponentProps) => {
  const {
    customer,
    template,
    pricing,
    actions: { setIsFormValid },
  } = useContext(CheckoutContext);
  const [zipCodeValid, setZipCodeValid] = useState(false);

  // ZIP/postal input is shown for US/CA customers so they can view/correct
  // their location — EXCEPT on templates that opt into `hidePrefilledZipCode`
  // (PREFERRED / last-payment), where a returning depositor's ZIP is already
  // on file (`hasStoredZipCode`) AND that ZIP has already produced valid tax
  // (`isTaxValid`). If tax is invalid/not yet computed we keep the field so the
  // shopper can correct it (otherwise they'd be stuck with a bad tax total).
  const isTaxValid = pricing?.priceBreakdown?.isTaxValid;
  const shouldShowZipCode =
    shouldShowZipCodeFieldForTaxCalculation(customer) &&
    !(template.features.hidePrefilledZipCode && hasStoredZipCode(customer) && isTaxValid);

  useEffect(() => {
    const isValid = shouldShowZipCode ? zipCodeValid : true;

    setIsFormValid(isValid);
    if (onValidate) {
      onValidate(isValid);
    }
  }, [shouldShowZipCode, zipCodeValid, setIsFormValid, onValidate]);

  const handleZipCodeValidate = useCallback(
    (isValid: boolean) => {
      setZipCodeValid(isValid);
      if (onZipCodeValidate) {
        onZipCodeValidate(isValid);
      }
    },
    [onZipCodeValidate]
  );

  if (!shouldShowZipCode) {
    return null;
  }

  return <ZipCode onValidate={handleZipCodeValidate} isBlockedForZipCode={isBlockedForZipCode} />;
};
